import React from "react";
import { useState, useEffect } from "react";

interface InputProps {
  label?: string;
  name?: string;
  value?: string;
  placeholder?: string;
  className?: string;
  error?: boolean;
  errorMessage?: string;
  helpText?: string;
  isDisabled?: boolean;
  variant?: "text" | "number" | "date" | "email";
  required?: boolean
  pattern?: string;
  onChange?: (name: string, value: string | number, hasError?: boolean) => void;
  min?: string
  max?: string
}

const InputComp = (props: InputProps) => {
  const {
    label,
    name,
    value,
    placeholder,
    className,
    error,
    errorMessage,
    helpText,
    isDisabled = false,
    variant = "text",
    required,
    pattern,
    onChange,
    min,
    max,
  } = props;

  const [localValue, setLocalValue] = useState(value);
  const [isDirty, setIsDirty] = useState(false);
  const [lastConfirmedValue, setLastConfirmedValue] = useState(value);

  useEffect(() => {
    if (!isDirty && value !== localValue) {
      setLocalValue(value);
    }
  }, [value]);
  const handleBlurEvent = (e: React.FocusEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const inputName = e.target.name;
    setIsDirty(false); // done typing
    if (inputName === "fromDate" || inputName === "toDate") {
      const inputDate = new Date(inputValue);
      const minDate = new Date(min);
      const maxDate = new Date(max);
      if (inputDate < minDate) {
        const correctedDate = min;
        setLocalValue(correctedDate);
        setLastConfirmedValue(correctedDate); // update confirmed value
        if (inputName === "fromDate")
          onChange(inputName, correctedDate, true); // send corrected value to parent and display msg
        else
          onChange(inputName, correctedDate, null); // send corrected value to parent and no change in display msg
      }
      else if (inputDate > maxDate) {
        const correctedDate = max;
        setLocalValue(correctedDate);
        onChange(inputName, correctedDate); // send corrected value to parent
        setLastConfirmedValue(correctedDate); // update confirmed value
      }
      else {
        //   onChange(inputName, inputValue); // valid date
        // Check if the input is different from last confirmed value
        if (inputValue !== lastConfirmedValue) {
          setLocalValue(inputValue);
          setLastConfirmedValue(inputValue); // update confirmed value
          if (inputName === "fromDate"){
            onChange(inputName, inputValue, false); // manually changed
          }
          else{
            onChange(inputName, inputValue, null); // manually changed
          }
        } else {
          // No change — keep error message
          onChange(inputName, inputValue, null); // unchanged
        }

      }
    }
    else {
      onChange(inputName, inputValue); // other fields
    }
  };


  return (
    <div className={`${className ? className : ""} py-16`}>
      {label && (
        <label htmlFor={name} className="block text-neutral-100 text-md mb-8">
          {label}
        </label>
      )}
      <input
        id={name}
        data-testid="input"
        type={variant}
        pattern={pattern}
        name={name}
        className={`border rounded-md w-100 p-16 text-neutral-90
                    hover:outline-none 
                    hover:border-purple-100
                    hover:text-neutral-100 
                    active:outline-none 
                    active:border-purple-100
                    active:text-neutral-100
                    mb-8 ${error ? "border-danger-100 invalid" : "border-neutral-50"
          }`}
        placeholder={placeholder}
        disabled={isDisabled}
        required={required}
        //  value={value}
        value={localValue}
        //  onChange={(e) => onChange(e.target.name, e.target.value)}
        onChange={(e) => {
          const inputValue = e.target.value;
          const inputName = e.target.name;
          if (inputName === "fromDate" || name === "toDate") {
            setIsDirty(true);
            const inputDate = new Date(inputValue);
            const minDate = new Date(min);
            const maxDate = new Date(max);
            //    if (inputDate >= minDate) {
            if (inputDate >= minDate && inputDate <= maxDate) {
              onChange(inputName, inputValue,null); // valid selection from picker
            }
            setLocalValue(inputValue);
          }
          else {
            // For all other fields, just pass the value
            setLocalValue(inputValue);
            onChange(inputName, inputValue);
          }
        }}
        onBlur={handleBlurEvent} // validate on blur
        autoComplete={
          name === "fromDate" || name === "toDate" ? "transaction-date" : "off"
        }
        min={min}
        max={max}
      />
      {error && errorMessage && (
        <small className={`block text-danger-100 text-base mb-8`}>
          {errorMessage}
        </small>
      )}
      {helpText && (
        <small className="block text-neutral-100 text-base">{helpText}</small>
      )}
    </div>
  );
};

export default InputComp;
